import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { reportError } from '../lib/errorReporting';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [logoutReason, setLogoutReason] = useState(null); // 'inactivity' | 'deploy' | null

  const logout = useCallback((reason = 'manual') => {
    setLogoutReason(reason || null);
    setSessionExpired(reason === 'inactivity' || reason === 'deploy');
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        const url = error.config?.url || '';
        const status = error.response?.status;

        // 401 ya tiene su propio manejo (logout) — no es un bug a reportar,
        // ni siquiera cuando es un intento de login con credenciales inválidas.
        if (status === 401) {
          if (!url.includes('/auth/login')) {
            logout('deploy');
          }
          return Promise.reject(error);
        }

        // 403/500/red: posibles bugs reales. 400/404/422 se dejan fuera porque
        // normalmente son validaciones esperadas que cada pantalla ya maneja.
        const isNetworkError = !error.response;
        const isServerError = status >= 500;
        const isForbidden = status === 403;
        if ((isNetworkError || isServerError || isForbidden) && !url.includes('/error-logs')) {
          reportError({
            error_message: error.message || `Error ${status || 'de red'} en ${url}`,
            stack_trace: error.stack || null,
            endpoint: url,
            extra_context: { status: status || null, method: error.config?.method || null },
          });
        }

        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return userData;
  };

  const isMaster    = user?.role === 'master';
  const isAdmin     = user?.role === 'admin' || user?.role === 'master'; // master hereda todo de admin
  const isAgente    = user?.role === 'agente';
  const isAspirante = user?.role === 'aspirante';
  const isAsesor    = user?.role === 'asesor';
  const isViewer    = user?.role === 'viewer';
  const isDeveloper = user?.role === 'developer';
  const isAsistente = user?.role === 'asistente';
  const isAgentLike = ['agente', 'asistente', 'developer'].includes(user?.role);
  const canEdit = isAdmin || isAgente || isAspirante || isAsistente || isDeveloper;
  const canCreate = isAdmin || isAgente || isAspirante || isAsistente || isDeveloper;
  const canDelete = isAdmin || isDeveloper;

  const resetInactivityTimer = useCallback(() => {}, []); // no-op; real implementation in Layout.js

  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      token,
      loading,
      login,
      logout,
      register,
      refreshUser,
      sessionExpired,
      setSessionExpired,
      logoutReason,
      setLogoutReason,
      resetInactivityTimer,
      isMaster,
      isAdmin,
      isAgente,
      isAspirante,
      isAsesor,
      isViewer,
      isDeveloper,
      isAsistente,
      isAgentLike,
      canEdit,
      canCreate,
      canDelete
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
